
import { supabase } from "@/integrations/supabase/client";

// Convert NMEA format (ddmm.mmmm) to decimal degrees
const convertNMEAToDecimal = (nmeaValue: number | null, direction: string | null = null): number | null => {
  if (nmeaValue === null) return null;
  
  // Convert to string to handle decimal places properly
  const str = nmeaValue.toString();
  // Extract degrees and minutes
  const degrees = parseInt(str.slice(0, 2));
  const minutes = parseFloat(str.slice(2));
  
  // Convert to decimal degrees
  let decimalDegrees = degrees + (minutes / 60);
  if (direction === 'S') decimalDegrees = -decimalDegrees;
  
  // Validate the result
  if (decimalDegrees < -90 || decimalDegrees > 90) {
    console.warn(`Invalid latitude value after conversion: ${decimalDegrees}`);
    return null;
  }
  
  return decimalDegrees;
};

// Convert NMEA longitude format (dddmm.mmmm) to decimal degrees
const convertNMEALongitudeToDecimal = (nmeaValue: number | null, direction: string | null): number | null => {
  if (nmeaValue === null) return null;
  
  // Convert to string to handle decimal places properly
  const str = nmeaValue.toString();
  // For longitude, first 3 digits are degrees
  const degrees = parseInt(str.slice(0, 3));
  const minutes = parseFloat(str.slice(3));
  
  // Convert to decimal degrees
  let decimalDegrees = degrees + (minutes / 60);
  
  // Apply negative value for West coordinates
  if (direction === 'W') {
    decimalDegrees = -decimalDegrees;
  }
  
  // Validate the result
  if (decimalDegrees < -180 || decimalDegrees > 180) {
    console.warn(`Invalid longitude value after conversion: ${decimalDegrees}`);
    return null;
  }
  
  return decimalDegrees;
};

export const syncDeviceDetails = async (deviceId: string, apiResponse: any) => {
  try {
    console.log("Syncing device details for:", deviceId, "API response:", apiResponse);
    
    // Extract location data from GNSS if available
    const rawLat = apiResponse.gnss?.latitude || null;
    const rawLong = apiResponse.gnss?.longitude || null;
    const eastWest = apiResponse.gnss?.eastWest || null;
    const northSouth = apiResponse.gnss?.northSouth || null;
    
    // Convert coordinates from NMEA format to decimal degrees
    const lat = convertNMEAToDecimal(rawLat, northSouth);
    const long = convertNMEALongitudeToDecimal(rawLong, eastWest);
    
    console.log("Raw coordinates:", { rawLat, rawLong, eastWest });
    console.log("Converted coordinates:", { lat, long });

    // Extract contract image data - only if explicitly provided
    const imagesAllowed = apiResponse.contract?.imagesAllowed;
    const imagesCurrent = apiResponse.contract?.imagesCurrent;
    const imagesOverflow = apiResponse.contract?.imagesOverflow;

    console.log("Contract image data:", { imagesAllowed, imagesCurrent, imagesOverflow });

    const {
      deviceName,
      deviceDescription,
      deviceLastUpdated,
      deviceStatus,
      deviceFirmware,
      deviceSerial,
      notification,
      imageKit,
      cellular,
      battery,
      gnss,
      location,
      currentHdop,
      notificationAiModel,
      cameraConfig,
      imageList,
      pastImages,
    } = apiResponse;

    // Parse imageList (may be JSON string or array) for past_images persistence
    let parsedImageList: any[] | null = null;
    const rawImageList = imageList ?? pastImages;
    if (rawImageList != null) {
      if (typeof rawImageList === 'string') {
        try { parsedImageList = JSON.parse(rawImageList); } catch { parsedImageList = null; }
      } else if (Array.isArray(rawImageList)) {
        parsedImageList = rawImageList;
      } else if (typeof rawImageList === 'object') {
        // Handle object-keyed format: { image1: "url1", image2: "url2", ... }
        parsedImageList = Object.entries(rawImageList)
          .sort(([a], [b]) => a.localeCompare(b, undefined, { numeric: true }))
          .map(([_, url]) => url);
      }
    }

    // Log the notification AI model from the API response
    console.log("Notification AI model from API:", notificationAiModel);

    // Fetch existing JSON fields to preserve non-null values
    let existingCellular: any = null;
    let existingCameraConfig: any = null;
    try {
      const { data: existingRows } = await supabase
        .from('device_details')
        .select('cellular, camera_config')
        .eq('device_id', deviceId)
        .limit(1);
      if (existingRows && existingRows.length > 0) {
        existingCellular = existingRows[0].cellular || null;
        existingCameraConfig = existingRows[0].camera_config || null;
      }
    } catch (e) {
      console.log('Failed to fetch existing device_details for merge:', e);
    }

    // Null-safe merge helper
    const mergePreservingNonNull = (existing: any, incoming: any): any => {
      if (incoming === null || incoming === undefined) return existing ?? null;
      if (Array.isArray(incoming)) return incoming;
      if (typeof incoming !== 'object' || incoming === null) return incoming;
      const out: any = (existing && typeof existing === 'object' && !Array.isArray(existing)) ? { ...existing } : {};
      for (const key of Object.keys(incoming)) {
        const v = incoming[key];
        if (v === null || v === undefined) continue;
        out[key] = mergePreservingNonNull(out[key], v);
      }
      return out;
    };

    // Compute merged JSON payloads
    const mergedCellular = mergePreservingNonNull(existingCellular, cellular);
    const mergedCameraConfig = mergePreservingNonNull(existingCameraConfig, cameraConfig);

    // Log merge decisions (keys preserved/updated)
    try {
      const incomingCell = cellular || {};
      const skippedNullCellKeys = Object.keys(incomingCell).filter(k => incomingCell[k] == null && existingCellular?.[k] != null);
      const updatedCellKeys = Object.keys(incomingCell).filter(k => incomingCell[k] != null);
      console.log('DeviceSync merge cellular:', { updatedCellKeys, skippedNullCellKeys });
    } catch {}

    // Prepare upsert data - base data that all installations should have
    const upsertData: any = {
      device_id: deviceId,
      device_name: deviceName,
      device_description: deviceDescription,
      device_last_updated: deviceLastUpdated,
      device_status: deviceStatus,
      device_firmware: deviceFirmware,
      device_serial: deviceSerial,
      notification,
      imagekit: imageKit,
      cellular: mergedCellular,
      battery,
      gnss,
      location,
      current_hdop: currentHdop,
      lat_wgs84: lat,
      long_wgs84: long,
      camera_config: mergedCameraConfig,
      last_synced_at: new Date().toISOString()
    };

    // Only persist past_images if we have a non-empty array
    if (parsedImageList && parsedImageList.length > 0) {
      upsertData.past_images = parsedImageList;
    }

    // Only update image quota fields if explicitly provided in API response
    if (imagesAllowed !== undefined) {
      upsertData.images_allowed = imagesAllowed;
    }
    if (imagesCurrent !== undefined) {
      upsertData.images_current = imagesCurrent;
    }
    if (imagesOverflow !== undefined) {
      upsertData.images_overflow = imagesOverflow;
    }

    // Try to store the AI model value if the column exists
    try {
      // Check if column exists by trying a simple query
      const { data, error } = await supabase
        .from('device_details')
        .select('notification_ai_model')
        .limit(1);

      // If no error, the column exists, so include it in the upsert
      if (!error) {
        console.log("notification_ai_model column exists, including in upsert");
        upsertData.notification_ai_model = notificationAiModel || null;
      } else {
        console.log("notification_ai_model column does not exist yet:", error.message);
        // Simply log the error - the migration should handle column creation
        console.log("The column will be created by the migration.");
      }
    } catch (columnCheckError) {
      console.log("Error checking for column existence:", columnCheckError);
    }

    // Remove the deviceLocation field from the upsert call since it doesn't exist in the database
    const { error } = await supabase
      .from('device_details')
      .upsert(upsertData, {
        onConflict: 'device_id'
      });

    if (error) {
      console.error('Error syncing device details:', error);
      throw error;
    }

    console.log('Successfully synced device details for:', deviceId, 'with data:', { 
      imagesAllowed, 
      imagesCurrent, 
      imagesOverflow,
      notificationAiModel,
      cameraConfig // Log the camera config during successful sync
    });
  } catch (error) {
    console.error('Error in syncDeviceDetails:', error);
    throw error;
  }
};
